import {objectFromEntries} from '@benchling/js-utils';
import {SimpleGrid, Stack} from '@mantine/core';
import React, {useState, useEffect, useCallback, useMemo} from 'react';

import {AnalysisTemplateSelectorTemplate} from 'analysis-tool/graphql/AnalysisTemplateSelectorQuery';
import MultiAnalysisTemplateSelector from 'analysis-tool/templates/MultiAnalysisTemplateSelector';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {Section} from 'constants/NavConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderSelector from 'file-browser/FolderSelector';
import {BioentitySummary} from 'folder-items/FileConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import HelpButton from 'help/HelpButton';
import {isFileProcessingTemplate} from 'instruments/analysis-templates/AnalysisTemplatesEditableField';
import ConnectionFieldsForm from 'instruments/connections/ConnectionFieldsForm';
import {
  applyAutoSyncLogic,
  buildAdapterFields,
  getInitialValues,
} from 'instruments/connections/ConnectionFieldsUtils';
import EquipmentSearchInput from 'instruments/equipment/EquipmentSearchInput';
import {InstrumentGateway} from 'instruments/instrument-gateways/InstrumentGatewayConstants';
import notifications from 'notifications';
import {OrganizationSelectorQuery} from 'orgs/__generated/OrganizationSelectorQuery';
import OrganizationSelector, {Organization, ORGANIZATION_SELECTOR_QUERY} from 'orgs/OrganizationSelector';
import {getStore} from 'redux-stores/Store';
import {forceRefreshSearch} from 'search/SearchUIReduxActions';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {GENERIC_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useOnMount from 'ui-library/hooks/useOnMount';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Link from 'ui-library/Link';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';
import * as logging from 'util/Logging';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {listGateways} from '../instrument-gateways/InstrumentGatewayDAO';
import {AdapterField, ConnectionFieldType, InstrumentAdapter} from './ConnectionConstants';
import {createConnection, getAdapter, listInstrumentTypes, listAdapters} from './ConnectionDAO';

interface CreateConnectionModalProps {
  onRequestClose: () => void;
}

export const emptyGatewayOptionLabel = 'Select gateway';

export default function CreateConnectionModal(props: CreateConnectionModalProps): JSX.Element {
  const [instrumentTypes, setInstrumentTypes] = useState<Array<InstrumentAdapter> | null>(null);
  const [selectedAdapterId, setSelectedAdapterId] = useState<string | null>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

  const [allGateways, setAllGateways] = useState<Array<InstrumentGateway> | null>(null);
  const [gatewayTypesByAdapterId, setGatewayTypesByAdapterId] = useState<Record<string, Set<string>>>({});
  const [name, setName] = useState('');
  const [gatewayId, setGatewayId] = useState<string | null>(null);
  const [dataCatalogFolderId, setDataCatalogFolderId] = useState<string | null>(null);
  const [isPosting, setIsPosting] = useState(false);
  const [adapterFields, setAdapterFields] = useState<Array<AdapterField>>([]);
  const [gatewayFields, setGatewayFields] = useState<Array<AdapterField>>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fieldInfos, setFieldInfos] = useState({} as {[id: string]: string | null});
  const [equipment, setEquipment] = useState<Array<BioentitySummary>>([]);
  const [analysisTemplates, setAnalysisTemplates] = useState<Array<AnalysisTemplateSelectorTemplate>>([]);

  const orgQueryResult = useBenchlingQuery<OrganizationSelectorQuery>(ORGANIZATION_SELECTOR_QUERY);
  const isSingleOrgTenant =
    orgQueryResult.type === QueryResultType.SUCCESS &&
    orgQueryResult.data.currentTenantOrganizations.length === 1;

  // Auto-select the only organization when there's exactly one on the tenant
  useEffect(() => {
    if (
      isSingleOrgTenant &&
      selectedOrganization == null &&
      orgQueryResult.type === QueryResultType.SUCCESS
    ) {
      const org = orgQueryResult.data.currentTenantOrganizations[0];
      setSelectedOrganization({id: org.id, handle: org.handle, name: org.name});
    }
  }, [isSingleOrgTenant, selectedOrganization, orgQueryResult]);

  const handleAnalysisTemplateChange = useCallback((templates: Array<AnalysisTemplateSelectorTemplate>) => {
    setAnalysisTemplates(templates);
  }, []);

  const handleSubmit = async (): Promise<void> => {
    assert(canSubmit, 'Cannot call submission handler when state is invalid for submission');
    setIsPosting(true);
    try {
      const {id: connectionId} = await withLoadingScreen(
        createConnection({
          adapterId: selectedAdapterId,
          ownerId: selectedOrganization.id,
          name,
          gatewayId,
          dataCatalogFolderId: dataCatalogFolderId ?? undefined,
          adapterFields: buildAdapterFields(fieldValues),
          associatedEquipmentIds: equipment.map((item) => item.id),
          automationTemplateIds: analysisTemplates.map((template) => template.id),
        }),
        'Creating connection...'
      );
      FileBrowserActions.openFile(connectionId);
      getStore().dispatch(forceRefreshSearch(Section.CONNECTIONS));
      props.onRequestClose();
    } catch (err) {
      setIsPosting(false);
      if (net.isServerRequestError(err)) {
        notifications.notifyError(err);
      } else {
        throw err;
      }
    }
  };

  // Load gateways and instrument types on mount. Gateways are needed first (user selects
  // gateway before instrument type), so they gate initial render. Instrument types and
  // adapter details load in parallel but don't block the modal.
  useOnMount(() => {
    Promise.all([
      listGateways().then(({gateways}) => {
        setAllGateways(gateways.filter((gateway) => !gateway.isArchived));
      }),
      listInstrumentTypes().then(({instrumentAdapters}) => {
        setInstrumentTypes(instrumentAdapters);
        // Preload supported gateway types for every adapter
        instrumentAdapters.forEach((adapter) => {
          listAdapters(adapter.id).then(({instrumentAdapters: adapterDetails}) => {
            setGatewayTypesByAdapterId((prev) => ({
              ...prev,
              [adapter.id]: new Set<string>(
                adapterDetails.flatMap((a) => (a.gatewayType ? [a.gatewayType] : []))
              ),
            }));
          });
        });
      }),
    ]).catch((err) => {
      // Loading gateways/instrument types from the instruments service failed. This is
      // unexpected and not user-actionable, so show a generic toast and report to Sentry.
      // The gateway/instrument-type dropdowns remain disabled (their state stays null).
      notifications.error(GENERIC_ERROR_MESSAGE);
      logging.error(err);
    });
  });

  // Map all gateways by id for easy lookup
  const allGatewaysById = useMemo<{[id: string]: InstrumentGateway} | null>(() => {
    if (!allGateways) {
      return null;
    }
    return objectFromEntries(allGateways.map((gw) => [gw.id, gw]));
  }, [allGateways]);

  const selectedGatewayType =
    gatewayId && allGatewaysById ? allGatewaysById[gatewayId]?.gatewayType : undefined;

  // True once all adapter details have been fetched
  const allAdapterDetailsLoaded =
    instrumentTypes != null && instrumentTypes.every((a) => gatewayTypesByAdapterId[a.id] != null);

  // Filter instrument types to those compatible with the selected gateway
  const filteredInstrumentTypes = useMemo<Array<InstrumentAdapter> | null>(() => {
    if (!instrumentTypes || !selectedGatewayType) {
      return null;
    }
    return instrumentTypes.filter((adapter) => {
      const types = gatewayTypesByAdapterId[adapter.id];
      return types && types.has(selectedGatewayType);
    });
  }, [instrumentTypes, selectedGatewayType, gatewayTypesByAdapterId]);

  // Auto-select instrument type if only one matches (wait for all adapter details to avoid
  // premature selection while results are still loading in)
  useEffect(() => {
    if (allAdapterDetailsLoaded && filteredInstrumentTypes && filteredInstrumentTypes.length === 1) {
      setSelectedAdapterId(filteredInstrumentTypes[0].id);
    }
  }, [allAdapterDetailsLoaded, filteredInstrumentTypes]);

  // Auto-select gateway if only one available
  useEffect(() => {
    if (allGateways && allGateways.length === 1) {
      setGatewayId(allGateways[0].id);
    }
  }, [allGateways]);

  // When gateway or adapter changes, load adapter fields
  useEffect(() => {
    if (allGatewaysById && gatewayId && selectedAdapterId) {
      setAdapterFields([]);
      setGatewayFields([]);
      setFieldValues({});
      setFieldInfos({});
      getAdapter(selectedAdapterId, allGatewaysById[gatewayId].gatewayType).then((adapter) => {
        const textAndSecretFields = adapter.fields.filter(
          (field) =>
            field.fieldType === ConnectionFieldType.TEXT || field.fieldType === ConnectionFieldType.SECRET_REF
        );
        const gwFields = adapter.fields.filter((field) => field.fieldType === ConnectionFieldType.GATEWAY);
        const gatewayFields_ = allGatewaysById[gatewayId].fields || {};
        const gatewayFieldOverrides: Record<string, string> = Object.fromEntries(
          gwFields
            .filter((field) => gatewayFields_[field.id] != null)
            .map((field) => [field.id, gatewayFields_[field.id]!])
        );
        setAdapterFields(textAndSecretFields);
        setGatewayFields(gwFields);
        setFieldValues(
          getInitialValues([...gwFields, ...textAndSecretFields], undefined, gatewayFieldOverrides)
        );
        setFieldInfos(Object.fromEntries(adapter.fields.map((field) => [field.id, field.info])));
      });
    }
  }, [gatewayId, selectedAdapterId, allGatewaysById]);

  // Render the modal shape immediately rather than blocking on the instruments-service calls.
  // The gateway and instrument-type dropdowns show a disabled "loading" state until their data
  // arrives (or stay disabled if the request failed).
  const areGatewaysLoading = allGateways == null;

  const hasRequiredFolderId = dataCatalogFolderId != null;
  const isInstrumentTypeDisabled = areGatewaysLoading || !gatewayId;
  const isInstrumentTypeLoading =
    gatewayId != null &&
    (instrumentTypes == null ||
      // Per-adapter gateway-type details load asynchronously and feed filteredInstrumentTypes.
      // Keep the dropdown in a loading state until they've all arrived, otherwise it can briefly
      // appear enabled with an empty/partial menu (looks like "no instrument types").
      (selectedGatewayType != null && (filteredInstrumentTypes == null || !allAdapterDetailsLoaded)));
  const areFieldsReady = gatewayId != null && (gatewayFields.length > 0 || adapterFields.length > 0);
  const hasCredentialsForAutoSync =
    fieldValues.per_user_login_required !== 'true' ||
    Boolean(fieldValues.username?.trim() && fieldValues.password?.trim());

  const canSubmit =
    selectedAdapterId != null &&
    selectedOrganization != null &&
    gatewayId != null &&
    hasRequiredFolderId &&
    name.trim().length !== 0 &&
    !isPosting;

  return (
    <Modal
      title="Create new connection"
      mediumLarge={true}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      buttons={[
        {
          text: isPosting ? 'Creating...' : 'Create',
          submit: true,
          disabled: !canSubmit,
        },
      ]}
      analyticsModule={ANALYTICS_MODULE.CONNECT}
      analyticsModalType="create_connection"
    >
      <Stack spacing="md">
        <SimpleGrid cols={2}>
          <TextField
            label="Connection Name"
            labelStyle={LabelStyle.LABEL}
            onChange={(e) => {
              setName(e.target.value);
            }}
            placeholder="Name"
            value={name}
            isRequired={true}
          />
        </SimpleGrid>
        <SimpleGrid cols={2}>
          <div>
            <FieldLabel isRequired={true} label="Benchling Gateway" labelStyle={LabelStyle.LABEL} />
            <SelectDropdown
              disabled={areGatewaysLoading}
              menu={
                <SearchableMenu
                  selectedValue={gatewayId}
                  optionDatas={[
                    {name: emptyGatewayOptionLabel, value: null},
                    ...(allGateways ?? []).map((gw) => ({
                      name: gw.name,
                      value: gw.id,
                    })),
                  ]}
                  onSelect={(newGatewayId: string | null) => {
                    setGatewayId(newGatewayId);
                    setSelectedAdapterId((prev) => {
                      if (!prev || !newGatewayId) {
                        return null;
                      }
                      const gateway = allGatewaysById?.[newGatewayId];
                      if (!gateway) {
                        return null;
                      }
                      const types = gatewayTypesByAdapterId[prev];
                      return types && types.has(gateway.gatewayType) ? prev : null;
                    });
                  }}
                  height={Menu.HEIGHTS.MEDIUM}
                  width={Menu.WIDTHS.FULL}
                  scrollable={true}
                />
              }
              displayName={
                areGatewaysLoading
                  ? 'Loading...'
                  : ((gatewayId && allGatewaysById?.[gatewayId]?.name) ?? 'Select gateway')
              }
            />
          </div>
          <div>
            <FieldLabel isRequired={true} label="Benchling Location" labelStyle={LabelStyle.LABEL} />
            <HelpButton
              renderContent={() =>
                'Files will be stored and can be accessed through this folder inside of Benchling.'
              }
              iconClassName="u-mini-spacing-left"
            />
            <FolderSelector
              value={dataCatalogFolderId}
              onChange={(e: {target: {value: string}}) => {
                setDataCatalogFolderId(e.target.value);
              }}
              unsetOnFetchFail={true}
            />
          </div>
        </SimpleGrid>
        <SimpleGrid cols={2}>
          <div>
            <FieldLabel isRequired={true} label="Connection Type" labelStyle={LabelStyle.LABEL} />
            <HelpButton
              renderContent={() =>
                'Available connection types are determined by the type of gateway selected.'
              }
              iconClassName="u-mini-spacing-left"
            />
            <Tooltip
              text="Select a gateway to show available connection types"
              isDisabled={!isInstrumentTypeDisabled || isInstrumentTypeLoading}
              toggleDisplayMode={ToggleDisplayMode.BLOCK}
            >
              <SelectDropdown
                disabled={isInstrumentTypeDisabled || isInstrumentTypeLoading}
                menu={
                  <SearchableMenu
                    selectedValue={selectedAdapterId}
                    optionDatas={
                      filteredInstrumentTypes
                        ? filteredInstrumentTypes.map((adapter) => ({
                            name: adapter.displayName,
                            value: adapter.id,
                          }))
                        : []
                    }
                    onSelect={(adapterId: string | null) => {
                      setSelectedAdapterId(adapterId);
                    }}
                    height={Menu.HEIGHTS.MEDIUM}
                    width={Menu.WIDTHS.FULL}
                    scrollable={true}
                  />
                }
                displayName={
                  isInstrumentTypeLoading
                    ? 'Loading...'
                    : (instrumentTypes?.find((a) => a.id === selectedAdapterId)?.displayName ??
                      'Select connection type')
                }
              />
            </Tooltip>
          </div>
          {!isSingleOrgTenant && (
            <div>
              <FieldLabel isRequired={true} label="Organization" labelStyle={LabelStyle.LABEL} />
              <OrganizationSelector
                showOnlyUserOrgs={false}
                autoSelectOnlyOrg={true}
                selectedOrganization={selectedOrganization}
                onSelectOrganization={setSelectedOrganization}
              />
            </div>
          )}
        </SimpleGrid>
        {areFieldsReady && (
          <>
            {gatewayFields.length > 0 && (
              <ConnectionFieldsForm
                fields={gatewayFields}
                values={fieldValues}
                fieldInfos={fieldInfos}
                isWritable={false}
                isCreating={true}
                hasCredentialsForAutoSync={hasCredentialsForAutoSync}
                onChange={(fieldId, value) => {
                  setFieldValues((prev) => applyAutoSyncLogic(fieldId, value, prev));
                }}
              />
            )}
            {adapterFields.length > 0 && (
              <ConnectionFieldsForm
                fields={adapterFields}
                values={fieldValues}
                fieldInfos={fieldInfos}
                isWritable={true}
                isCreating={true}
                hasCredentialsForAutoSync={hasCredentialsForAutoSync}
                onChange={(fieldId, value) => {
                  setFieldValues((prev) => applyAutoSyncLogic(fieldId, value, prev));
                }}
              />
            )}
          </>
        )}
        {(BenchlingConfig.ENABLE_EQUIPMENT ||
          (BenchlingConfig.ENABLE_ANALYSIS_TOOL_TEMPLATES &&
            BenchlingConfig.PIPELINES_ENABLE_CONNECTION_INTEGRATION)) && (
          <SimpleGrid cols={2}>
            {BenchlingConfig.ENABLE_EQUIPMENT && (
              <div>
                <FieldLabel isRequired={false} label="Associated Equipment" labelStyle={LabelStyle.LABEL} />
                <EquipmentSearchInput
                  shouldAllowMultiple={true}
                  onChange={(equipment) => {
                    setEquipment(equipment as Array<BioentitySummary>);
                  }}
                  selectedItems={equipment}
                  autoFocus={false}
                />
              </div>
            )}
            {BenchlingConfig.ENABLE_ANALYSIS_TOOL_TEMPLATES &&
              BenchlingConfig.PIPELINES_ENABLE_CONNECTION_INTEGRATION && (
                <div>
                  <FieldLabel isRequired={false} label="Automation templates" labelStyle={LabelStyle.LABEL} />
                  <MultiAnalysisTemplateSelector
                    autoFocus={false}
                    filterTemplates={isFileProcessingTemplate}
                    selectedItems={analysisTemplates}
                    onChange={handleAnalysisTemplateChange}
                    shouldAllowMultiple={true}
                    hideSelectedResults={true}
                  />
                </div>
              )}
          </SimpleGrid>
        )}
        <Link
          href="https://help.benchling.com/hc/en-us/articles/22558210727565-Instruments-supported-by-Benchling-Connect"
          target="_blank"
        >
          Benchling Instrument Configuration Guides
        </Link>
      </Stack>
    </Modal>
  );
}
